import { lazy, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

// project-imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import useAuth from 'hooks/useAuth';
import { isTrialExclPlan } from 'utils/trialPlans';

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/error/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/error/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon/coming-soon')));

// render - overview
const OverviewResume = Loadable(lazy(() => import('pages/overview/resume')));
const OverviewMetrics = Loadable(lazy(() => import('pages/overview/metrics')));
// render - development company
const DevelopmentCompaniesPage = Loadable(lazy(() => import('pages/development-companies/development-companies')));
const DevelopmentCompaniesSpecialPage = Loadable(lazy(() => import('pages/development-companies/development-companies-special')));
// render - development
// const DevelopmentCard = Loadable(lazy(() => import('pages/developments/developments-card')));
const DevelopmentCardNew = Loadable(lazy(() => import('pages/developments/developments-card-new')));
// const DevelopmentTable = Loadable(lazy(() => import('pages/developments/developments-table')));
const DevelopmentTableNew = Loadable(lazy(() => import('pages/developments/developments-table-new')));
// const DevelopmentDetail = Loadable(lazy(() => import('pages/developments/development-detail')));
const DevelopmentCardSpecial = Loadable(lazy(() => import('pages/developments/developments-card-special')));
const DevelopmentCardInternational = Loadable(lazy(() => import('pages/developments/developments-card-international')));
const DevelopmentsInternationalTermsAndConditions = Loadable(
  lazy(() => import('pages/developments/developments-international-terms-and-conditions'))
);
const DevelopmentDetailV2 = Loadable(lazy(() => import('pages/developments/development-detail-v2')));
const PropertyDetailV2 = Loadable(lazy(() => import('pages/developments/property-detail-v2')));
// const DevelopmentTableNew = Loadable(lazy(() => import('pages/developments/developments-table')));
// render - customers
const CustomersPage = Loadable(lazy(() => import('pages/customers/customers')));
const CustomerPage = Loadable(lazy(() => import('pages/customers/customer/customer')));
const CustomerTabProfile = Loadable(lazy(() => import('sections/customers/customer/TabProfile')));
const CustomerTabSearchParams = Loadable(lazy(() => import('sections/customers/customer/TabSearchParams')));
const CustomerTabNotes = Loadable(lazy(() => import('sections/customers/customer/TabNotes')));
const CustomerTabOffers = Loadable(lazy(() => import('sections/customers/customer/TabOffers')));
const CustomerTabDT = Loadable(lazy(() => import('sections/customers/customer/TabDiscoveryTrip')));
const ContactFormsPage = Loadable(lazy(() => import('pages/customers/contact-forms')));
const ConnectRequestsPage = Loadable(lazy(() => import('pages/customers/connect-requests')));
// render - discovery trip
const DiscoveryTripsPage = Loadable(lazy(() => import('pages/discovery-trips/discovery-trips-table')));
const DiscoveryTripPage = Loadable(lazy(() => import('pages/discovery-trips/discovery-trip/discovery-trip')));
const DTTabResume = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabResume')));
const DTTabSearchParams = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabSearchParams')));
const DTTabNotes = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabNotes')));
const DTTabDevelopments = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabDevelopments')));
const DTTabIncidents = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabIncidents')));
const DTTabEvents = Loadable(lazy(() => import('sections/discovery-trips/discovery-trip/TabEvents')));
const DTSalesPage = Loadable(lazy(() => import('pages/discovery-trips/dt-sales-table')));
// render - tools
const OffersPage = Loadable(lazy(() => import('pages/tools/offers')));
const FeedPage = Loadable(lazy(() => import('pages/tools/feed')));
const AIPage = Loadable(lazy(() => import('pages/tools/ai')));
const AITabGenerator = Loadable(lazy(() => import('sections/ai/TabAIGenerator')));
const AITabTest = Loadable(lazy(() => import('sections/ai/TabAITest')));
const AITabSettings = Loadable(lazy(() => import('sections/ai/TabAISettings')));
// render - account
const AccountPage = Loadable(lazy(() => import('pages/account/account')));
const AccountTabProfile = Loadable(lazy(() => import('sections/account/TabProfile')));
const AccountTabUsers = Loadable(lazy(() => import('sections/account/TabUsers')));
// const AccountTabSubscription = Loadable(lazy(() => import('sections/account/TabSubscription')));
const AccountTabSubscription = Loadable(lazy(() => import('sections/account/TabSubscriptionNew')));
const AccountTabPassword = Loadable(lazy(() => import('sections/account/TabPassword')));
// const PaymentPage = Loadable(lazy(() => import('pages/account/payment')));
// render - marketplace
const MarketplacePage = Loadable(lazy(() => import('pages/marketplace/marketplace')));

const ProOnlyRoute = ({ children }: { children: ReactElement }) => {
  const { user } = useAuth();

  if (isTrialExclPlan(user?.currentPlan)) {
    return <Navigate to="/developments" replace />;
  }

  return children;
};

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'overview',
          element: <OverviewResume />
        },
        {
          path: 'metrics',
          element: <OverviewMetrics />
        },
        {
          path: 'developments',
          children: [
            {
              path: '',
              element: <DevelopmentCardNew />
            },
            {
              path: 'list',
              element: <DevelopmentTableNew />
            },
            {
              path: 'special',
              element: <DevelopmentCardSpecial />
            },
            {
              path: 'international',
              element: <DevelopmentCardInternational />
            },
            {
              path: 'international-terms-and-conditions',
              element: <DevelopmentsInternationalTermsAndConditions />
            },
            {
              path: ':id',
              children: [
                {
                  path: '',
                  element: <DevelopmentDetailV2 />
                },
                {
                  path: 'properties/:propertyId',
                  element: <PropertyDetailV2 />
                }
              ]
            }
          ]
        },
        {
          path: 'developments-new',
          children: [
            {
              path: ':id',
              element: <DevelopmentDetailV2 />
            }
          ]
        },
        {
          path: 'developments-v2',
          children: [
            {
              path: ':id',
              element: <DevelopmentDetailV2 />
            }
          ]
        },
        {
          path: 'development-companies',
          children: [
            {
              path: '',
              element: (
                <ProOnlyRoute>
                  <DevelopmentCompaniesPage />
                </ProOnlyRoute>
              )
            },
            {
              path: 'special',
              element: (
                <ProOnlyRoute>
                  <DevelopmentCompaniesSpecialPage />
                </ProOnlyRoute>
              )
            }
          ]
        },
        {
          path: 'customers',
          children: [
            {
              path: '',
              element: <CustomersPage />
            },
            {
              path: ':id',
              element: <CustomerPage />,
              children: [
                {
                  path: '',
                  element: <CustomerTabProfile />
                },
                {
                  path: 'search-params',
                  element: <CustomerTabSearchParams />
                },
                {
                  path: 'notes',
                  element: <CustomerTabNotes />
                },
                {
                  path: 'offers',
                  element: <CustomerTabOffers />
                },
                {
                  path: 'discovery-trips',
                  element: <CustomerTabDT />
                }
              ]
            }
          ]
        },
        {
          path: 'contact-forms',
          element: <ContactFormsPage />
        },
        {
          path: 'connect-requests',
          element: <ConnectRequestsPage />
        },
        {
          path: 'discovery-trip',
          children: [
            {
              path: '',
              element: <DiscoveryTripsPage />
            },
            {
              path: 'sales',
              element: <DTSalesPage />
            },
            {
              path: ':id',
              element: <DiscoveryTripPage />,
              children: [
                {
                  path: '',
                  element: <DTTabResume />
                },
                {
                  path: 'search-params',
                  element: <DTTabSearchParams />
                },
                {
                  path: 'notes',
                  element: <DTTabNotes />
                },
                {
                  path: 'developments',
                  element: <DTTabDevelopments />
                },
                {
                  path: 'incidents',
                  element: <DTTabIncidents />
                },
                {
                  path: 'activity',
                  element: <DTTabEvents />
                }
              ]
            }
          ]
        },
        {
          path: 'account',
          element: <AccountPage />,
          children: [
            {
              path: 'profile',
              element: <AccountTabProfile />
            },
            {
              path: 'users',
              element: <AccountTabUsers />
            },
            {
              path: 'subscription',
              element: <AccountTabSubscription />
            },
            {
              path: 'password',
              element: <AccountTabPassword />
            }
          ]
        },
        {
          path: 'marketplace',
          element: <MarketplacePage />
        },
        {
          path: 'tools',
          children: [
            {
              path: 'offers',
              element: <OffersPage />
            },
            {
              path: 'feed',
              element: <FeedPage />
            },
            {
              path: 'ai-generator',
              element: <AIPage />,
              children: [
                {
                  path: '',
                  element: <AITabGenerator />
                },
                {
                  path: 'test',
                  element: <AITabTest />
                },
                {
                  path: 'settings',
                  element: <AITabSettings />
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/maintenance',
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    }
  ]
};

export default MainRoutes;
